/**
 * Theme styles for ::define syntax — header, close, block lines,
 * L0 pretty widgets, status backgrounds, button group, and error message.
 */

import { EditorView } from "@codemirror/view";
import {
    CM_MONO_FONT_FAMILY,
    accentIconStyles,
    accentMutedColor,
    actionButtonStyles,
    directiveLineStyles,
    directivePrettyStyles,
    statusLineStyles,
} from "../shared/theme-helpers";

const DEFINE_ACCENT = "--cm-define-accent";
const RUNNING_ACCENT = "--status-running";
const SUCCESS_ACCENT = "--status-success";
const ERROR_ACCENT = "--status-error";

export const defineTheme = EditorView.baseTheme({
    /* Define block header */
    ".cm-magic-define-head": {
        color: `var(${DEFINE_ACCENT})`,
        fontWeight: "500",
        letterSpacing: "0.02em",
    },
    /* Define block close */
    ".cm-magic-define-close": {
        color: `var(${DEFINE_ACCENT})`,
        fontWeight: "500",
        opacity: "0.7",
    },
    /* Background for all lines within a define block */
    ".cm-magic-define-line": directiveLineStyles(DEFINE_ACCENT, 4, 25),
    /* L0: Pretty define widget — replaces raw ::define syntax */
    ".cm-magic-define-pretty": directivePrettyStyles(DEFINE_ACCENT),
    ".cm-magic-define-pretty-icon": accentIconStyles(DEFINE_ACCENT),
    ".cm-magic-define-pretty-plugin": {
        color: "var(--color-muted-foreground)",
        fontFamily: CM_MONO_FONT_FAMILY,
        fontSize: "12px",
        fontWeight: "400",
        lineHeight: "1.2",
        maxWidth: "180px",
        opacity: "0.76",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    ".cm-magic-define-pretty-cloud": {
        alignItems: "center",
        color: "var(--color-primary)",
        display: "inline-flex",
        height: "14px",
        justifyContent: "center",
        opacity: "0.86",
        width: "14px",
    },
    ".cm-magic-define-pretty-cloud svg": {
        display: "block",
        height: "12px",
        width: "12px",
    },
    ".cm-magic-define-pretty-template": {
        color: "var(--color-muted-foreground)",
        fontFamily: CM_MONO_FONT_FAMILY,
        fontSize: "11px",
        opacity: "0.68",
    },
    ".cm-magic-define-pretty-sep": {
        color: accentMutedColor(DEFINE_ACCENT, 55),
        fontSize: "11px",
        margin: "0 1px",
    },
    ".cm-magic-define-pretty-id": {
        color: `color-mix(in oklab, var(${DEFINE_ACCENT}) 86%, var(--color-foreground))`,
        fontWeight: "600",
        letterSpacing: "0.01em",
    },
    ".cm-magic-define-pretty-kind": {},
    /* Status-aware define line backgrounds */
    ".cm-magic-define-running": statusLineStyles(RUNNING_ACCENT, {
        backgroundPercent: 5,
        borderPercent: 50,
        animation: "cm-magic-border-breathe 2s ease-in-out infinite alternate",
    }),
    ".cm-magic-define-success": statusLineStyles(SUCCESS_ACCENT, {
        backgroundPercent: 6,
        borderPercent: 50,
    }),
    ".cm-magic-define-error": statusLineStyles(ERROR_ACCENT, {
        backgroundPercent: 6,
        borderPercent: 50,
    }),
    /* L2: show full badge in active block */
    ".cm-magic-define-active .cm-magic-badge": {
        display: "inline-flex",
    },
    ".cm-magic-define-active .cm-magic-status-dot": {
        display: "none !important",
    },
    /* Button group container — hidden by default, shown on hover (L1) */
    ".cm-magic-btn-group": {
        display: "inline-flex",
        alignItems: "center",
        position: "relative",
        zIndex: "2",
        marginLeft: "6px",
        borderRadius: "4px",
        overflow: "hidden",
        verticalAlign: "middle",
        border: `1px solid color-mix(in oklab, var(${DEFINE_ACCENT}) 25%, transparent)`,
        opacity: "0",
        maxWidth: "0",
        pointerEvents: "none",
        transition: "opacity 0.15s ease, max-width 0.2s ease",
    },
    /* Hover no longer upgrades widgets into a local L1 action state.
     * Hover semantics are now owned by Intelligence Hover cards. */
    ".cm-line:hover .cm-magic-btn-group": {
        opacity: "1",
        maxWidth: "176px",
        pointerEvents: "auto",
    },
    /* L2: always show buttons in active define block */
    ".cm-magic-define-active .cm-magic-btn-group": {
        opacity: "1",
        maxWidth: "176px",
        pointerEvents: "auto",
    },
    /* Shared button base */
    ".cm-magic-btn": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "22px",
        height: "20px",
        fontSize: "11px",
        cursor: "pointer",
        userSelect: "none",
        lineHeight: "1",
        transition: "all 0.15s",
    },
    /* Run button */
    ".cm-magic-btn-run": actionButtonStyles(DEFINE_ACCENT, {
        borderSide: "right",
    }),
    /* Stop button (shown when running) */
    ".cm-magic-btn-stop": {
        ...actionButtonStyles(ERROR_ACCENT, {
            borderSide: "right",
        }),
        fontSize: "11px",
    },
    /* Clear stale error button */
    ".cm-magic-btn-clear": actionButtonStyles(ERROR_ACCENT, {
        borderSide: "left",
    }),
    /* Config button */
    ".cm-magic-btn-config": actionButtonStyles(DEFINE_ACCENT),
    ".cm-magic-btn-inputs": {
        ...actionButtonStyles(DEFINE_ACCENT, {
            borderSide: "left",
        }),
        borderRight: `1px solid color-mix(in oklab, var(${DEFINE_ACCENT}) 18%, transparent)`,
    },
    /* Pin button */
    ".cm-magic-btn-pin": actionButtonStyles(DEFINE_ACCENT, {
        borderSide: "right",
    }),
    ".cm-magic-btn-inline-render": actionButtonStyles(DEFINE_ACCENT, {
        borderSide: "right",
    }),
    ".cm-magic-btn-config + .cm-magic-btn-inline-render": {
        borderLeft: `1px solid color-mix(in oklab, var(${DEFINE_ACCENT}) 18%, transparent)`,
    },
    ".cm-magic-btn-inline-render svg": {
        display: "block",
        width: "9px",
        height: "9px",
    },
    ".cm-magic-btn-inputs svg": {
        display: "block",
        width: "10px",
        height: "10px",
    },
    ".cm-magic-btn-active": {
        color: `var(${DEFINE_ACCENT})`,
        backgroundColor: `color-mix(in oklab, var(${DEFINE_ACCENT}) 14%, transparent)`,
    },
    /* Snapshot button — sits at the right end with a left divider */
    ".cm-magic-btn-snapshot": actionButtonStyles(DEFINE_ACCENT, {
        borderSide: "left",
    }),
    /* Inline error message — rendered as inline widget but displayed as block via CSS */
    ".cm-magic-error-msg": {
        display: "block",
        padding: "4px 12px",
        margin: "2px 0",
        backgroundColor:
            "color-mix(in oklab, var(--status-error) 8%, transparent)",
        borderLeft: "3px solid var(--status-error)",
        borderRadius: "0 4px 4px 0",
        fontSize: "12px",
        lineHeight: "1.4",
        fontFamily: CM_MONO_FONT_FAMILY,
    },
    ".cm-magic-error-icon": {
        color: "var(--status-error)",
        fontWeight: "500",
        fontSize: "11px",
        flexShrink: "0",
    },
    ".cm-magic-error-text": {
        color: "var(--status-error)",
        wordBreak: "break-word",
        flex: "1",
    },
    ".cm-magic-error-def": {
        color: "var(--color-muted-foreground)",
        fontSize: "11px",
        flexShrink: "0",
        marginLeft: "auto",
    },
});
